/**
 * Compact glyph button — a small square chip carrying a single icon glyph
 * (pencil, arrows). Shares the parchment / cyan-focus look of
 * `PauseMenuButton` but sized for overlay affordances such as the profile
 * avatar's edit pen and prev/next arrows. Clickable + focus-paintable.
 */

import Phaser from "phaser";

import { PALETTE, UGC_CYAN } from "../config";

const SIZE = 14;

export class IconButton {
  readonly container: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Rectangle;
  private focused = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    glyph: string,
    onActivate: () => void,
    size = SIZE,
  ) {
    this.container = scene.add.container(x, y).setDepth(45);

    this.bg = scene.add
      .rectangle(0, 0, size, size, PALETTE.pauseButtonBg, 0.95)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.pauseButtonEdge);

    const label = scene.add
      .text(0, 0, glyph, {
        fontFamily: "monospace",
        fontSize: `${Math.max(7, size - 6)}px`,
        color: `#${PALETTE.pauseButtonText.toString(16).padStart(6, "0")}`,
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    this.container.add([this.bg, label]);

    this.bg.setInteractive({ useHandCursor: true });
    this.bg.on("pointerdown", onActivate);
    this.bg.on("pointerover", () => this.setFocused(true));
    this.bg.on("pointerout", () => this.setFocused(false));

    this.paint();
  }

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    this.focused = focused;
    this.paint();
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  destroy(): void {
    this.container.destroy();
  }

  static readonly SIZE = SIZE;

  private paint(): void {
    if (this.focused) {
      this.bg.setStrokeStyle(2, UGC_CYAN);
      this.bg.setFillStyle(PALETTE.pauseButtonBg, 1);
    } else {
      this.bg.setStrokeStyle(1, PALETTE.pauseButtonEdge);
      this.bg.setFillStyle(PALETTE.pauseButtonBg, 0.95);
    }
  }
}
